// Handler factory for email template, folder, signature, attachment, and
// checklist CRUD — 15+ handlers consumed by App.js and passed down to email pages.

import { collection, doc } from 'firebase/firestore';
import { db } from '../firebase';
import * as emailService from '../services/emailService';
import { writeErrorMessage } from '../services/firestoreWrites';
import {
    createChecklistTemplate,
    updateChecklistTemplate,
    deleteChecklistTemplate,
} from '../services/checklistService';
import {
    SYSTEM_EMAIL_TEMPLATE_FOLDERS,
    normalizeTemplateTargetClients,
} from '../utils/emailTemplates';
import { normalizeChecklistTemplateNodes } from './useWorkspaceData';
import { slugify } from '../utils/helpers';
import { buildCreateTemplatePayload, buildImportTemplatePayload } from './emailTemplatePayloads';
import { validateEmailAttachmentFile } from '../utils/emailAttachmentValidation';

const normalizeChecklistTemplatePayload = (templateData = {}) => ({
    name: (templateData.name || '').trim(),
    description: templateData.description || '',
    items: normalizeChecklistTemplateNodes(templateData.items),
    targetClients: normalizeTemplateTargetClients(templateData.targetClients)
});

const extractExtension = (fileName = '') => {
    const trimmed = (fileName || '').trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === trimmed.length - 1) return '';
    return trimmed.slice(lastDot).toLowerCase();
};

const buildWorkspaceAssetPath = (workspaceId, fileName = '') => {
    const safeWorkspaceId = (workspaceId || '').trim();
    const inputName = typeof fileName === 'string' ? fileName : '';
    const lastDotIndex = inputName.lastIndexOf('.');
    const extension = lastDotIndex >= 0 ? inputName.slice(lastDotIndex).toLowerCase() : '';
    const base = lastDotIndex >= 0 ? inputName.slice(0, lastDotIndex) : inputName;
    const slugBase = slugify(base, { maxLength: 48 }) || 'asset';
    const uniqueSuffix = Math.random().toString(36).slice(2, 8);
    const timestamp = Date.now();
    return `workspaces/${safeWorkspaceId}/emailAssets/${timestamp}-${slugBase}-${uniqueSuffix}${extension}`;
};

export function useEmailTemplateHandlers({
    selectedWorkspaceId,
    user,
    emailTemplates,
    emailTemplateFolders,
    combinedTemplateFolders,
    run,
    showToast,
}) {
    const handleCreateEmailTemplate = async (templateData) => {
        if (!selectedWorkspaceId) return false;
        const payload = buildCreateTemplatePayload(templateData);
        return await run(async () => {
            await emailService.createEmailTemplate(selectedWorkspaceId, payload);
            showToast("Template saved!");
            return true;
        }, "Failed to save template.");
    };

    const handleUpdateEmailTemplate = async (templateId, templateData) => {
        if (!selectedWorkspaceId || !templateId) return false;
        const payload = {
            name: (templateData.name || '').trim(),
            subject: templateData.subject || '',
            html: templateData.html || '',
            description: templateData.description || '',
            attachmentIds: Array.isArray(templateData.attachmentIds) ? templateData.attachmentIds : [],
            updatedAt: Date.now(),
            folderId: typeof templateData.folderId === 'string' ? templateData.folderId : ''
        };
        return await run(async () => {
            await emailService.updateEmailTemplate(selectedWorkspaceId, templateId, payload);
            showToast("Template updated!");
            return true;
        }, "Failed to update template.");
    };

    const handleDeleteEmailTemplate = async (templateOrId) => {
        const templateId = typeof templateOrId === 'string' ? templateOrId : templateOrId?.id;
        const templateName = typeof templateOrId === 'string' ? '' : (templateOrId?.name || '').trim();
        if (!selectedWorkspaceId || !templateId) return false;
        const confirmed = window.confirm(
            templateName ? `Delete template "${templateName}"?` : "Delete this template?"
        );
        if (!confirmed) return false;
        return await run(async () => {
            await emailService.deleteEmailTemplate(selectedWorkspaceId, templateId);
            showToast("Template deleted.");
            return true;
        }, "Failed to delete template.");
    };

    const handleImportEmailTemplates = async (templatesToImport = []) => {
        if (!selectedWorkspaceId || !Array.isArray(templatesToImport) || templatesToImport.length === 0) return;
        const templates = templatesToImport.map(buildImportTemplatePayload);
        await run(async () => {
            await emailService.importEmailTemplates(selectedWorkspaceId, templates);
            showToast(`${templatesToImport.length} template${templatesToImport.length > 1 ? 's' : ''} imported.`);
        }, "Failed to import templates.");
    };

    const handleCreateChecklistTemplate = async (templateData) => {
        if (!selectedWorkspaceId) return false;
        const normalized = normalizeChecklistTemplatePayload(templateData);
        if (!normalized.name) {
            showToast('Checklist template name is required.', 'error');
            return false;
        }
        const payload = {
            ...normalized,
            createdAt: templateData.createdAt || Date.now(),
            updatedAt: templateData.updatedAt || Date.now()
        };
        return await run(async () => {
            await createChecklistTemplate(selectedWorkspaceId, payload);
            showToast('Checklist template saved!');
            return true;
        }, "Failed to save checklist template.");
    };

    const handleUpdateChecklistTemplate = async (templateId, templateData) => {
        if (!selectedWorkspaceId || !templateId) return false;
        const normalized = normalizeChecklistTemplatePayload(templateData);
        if (!normalized.name) {
            showToast('Checklist template name is required.', 'error');
            return false;
        }
        const payload = {
            ...normalized,
            updatedAt: Date.now()
        };
        return await run(async () => {
            await updateChecklistTemplate(selectedWorkspaceId, templateId, payload);
            showToast('Checklist template updated!');
            return true;
        }, "Failed to update checklist template.");
    };

    const handleDeleteChecklistTemplate = async (templateOrId) => {
        const templateId = typeof templateOrId === 'string' ? templateOrId : templateOrId?.id;
        const templateName = typeof templateOrId === 'string' ? '' : (templateOrId?.name || '').trim();
        if (!selectedWorkspaceId || !templateId) return false;
        const confirmed = window.confirm(
            templateName ? `Delete checklist template "${templateName}"?` : 'Delete this checklist template?'
        );
        if (!confirmed) return false;
        return await run(async () => {
            await deleteChecklistTemplate(selectedWorkspaceId, templateId);
            showToast('Checklist template deleted.');
            return true;
        }, "Failed to delete checklist template.");
    };

    const handleCreateEmailTemplateFolder = async (folderData = {}) => {
        if (!selectedWorkspaceId) return;
        const name = (folderData?.name || '').trim();
        if (!name) {
            showToast('Folder name is required.', 'error');
            return;
        }
        const targetClients = normalizeTemplateTargetClients(folderData?.targetClients);
        const normalizedName = name.toLowerCase();
        const exists = emailTemplateFolders.some(folder => (folder.name || '').trim().toLowerCase() === normalizedName)
            || SYSTEM_EMAIL_TEMPLATE_FOLDERS.some(folder => folder.name.toLowerCase() === normalizedName);
        if (exists) {
            showToast('A folder with that name already exists.', 'error');
            return;
        }
        const now = Date.now();
        const folderId = doc(collection(db, "workspaces", selectedWorkspaceId, "emailTemplates")).id;
        const folderPayload = { id: folderId, name, targetClients, createdAt: now, updatedAt: now };
        try {
            await emailService.addEmailTemplateFolder(selectedWorkspaceId, folderPayload);
            showToast('Folder created!');
        } catch (error) {
            console.error('Error creating template folder:', error);
            showToast(writeErrorMessage(error, 'Failed to create folder.'), 'error');
            throw error;
        }
    };

    const handleUpdateEmailTemplateFolder = async (folderId, updates = {}) => {
        if (!selectedWorkspaceId || !folderId) return;
        const folder = emailTemplateFolders.find(entry => entry.id === folderId);
        if (!folder) {
            showToast('Folder not found.', 'error');
            return;
        }
        const trimmedName = (typeof updates.name === 'string' ? updates.name : folder.name || '').trim();
        if (!trimmedName) {
            showToast('Folder name is required.', 'error');
            return;
        }
        const normalizedName = trimmedName.toLowerCase();
        const currentNameNormalized = (folder.name || '').trim().toLowerCase();
        if (normalizedName !== currentNameNormalized) {
            const duplicateExists = combinedTemplateFolders.some(entry => entry.id !== folderId && (entry.name || '').toLowerCase() === normalizedName);
            if (duplicateExists) {
                showToast('A folder with that name already exists.', 'error');
                return;
            }
        }
        const targetClients = normalizeTemplateTargetClients(
            updates.targetClients !== undefined ? updates.targetClients : folder.targetClients
        );
        const now = Date.now();
        const updatedFolders = emailTemplateFolders.map(entry => entry.id === folderId
            ? { ...entry, name: trimmedName, targetClients, updatedAt: now }
            : entry
        );
        try {
            await emailService.updateEmailTemplateFolders(selectedWorkspaceId, updatedFolders);
            showToast('Folder updated!');
        } catch (error) {
            console.error('Error updating template folder:', error);
            showToast(writeErrorMessage(error, 'Failed to update folder.'), 'error');
            throw error;
        }
    };

    const handleDeleteEmailTemplateFolder = async (folderId) => {
        if (!selectedWorkspaceId || !folderId) return;
        const folder = emailTemplateFolders.find(entry => entry.id === folderId);
        if (!folder) {
            showToast('Folder not found.', 'error');
            return;
        }
        try {
            const updatedFolders = emailTemplateFolders.filter(entry => entry.id !== folderId);
            await emailService.deleteEmailTemplateFolder(selectedWorkspaceId, updatedFolders, folderId);
            showToast('Folder deleted. Templates moved to Uncategorized.');
        } catch (error) {
            console.error('Error deleting template folder:', error);
            showToast(writeErrorMessage(error, 'Failed to delete folder.'), 'error');
            throw error;
        }
    };

    const handleMoveEmailTemplates = async (templateIds, targetFolderId) => {
        if (!selectedWorkspaceId || !Array.isArray(templateIds) || templateIds.length === 0) return;
        const uniqueTemplateIds = Array.from(new Set(templateIds.filter(Boolean)));
        if (uniqueTemplateIds.length === 0) return;
        const trimmedTarget = (targetFolderId || '').trim();
        try {
            await emailService.moveEmailTemplates(selectedWorkspaceId, uniqueTemplateIds, trimmedTarget);
            showToast(`Moved ${uniqueTemplateIds.length} template${uniqueTemplateIds.length === 1 ? '' : 's'}.`);
        } catch (error) {
            console.error('Error moving templates between folders:', error);
            showToast(writeErrorMessage(error, 'Failed to move templates.'), 'error');
            throw error;
        }
    };

    const handleCreateEmailSignature = async (signatureData) => {
        if (!selectedWorkspaceId) return;
        const payload = {
            name: (signatureData.name || '').trim(),
            html: signatureData.html || '',
            createdAt: Date.now(),
            updatedAt: Date.now()
        };
        await run(async () => {
            await emailService.createEmailSignature(selectedWorkspaceId, payload);
            showToast('Signature saved!');
        }, 'Failed to save signature.');
    };

    const handleUpdateEmailSignature = async (signatureId, signatureData) => {
        if (!selectedWorkspaceId || !signatureId) return;
        const payload = {
            name: (signatureData.name || '').trim(),
            html: signatureData.html || '',
            updatedAt: Date.now()
        };
        await run(async () => {
            await emailService.updateEmailSignature(selectedWorkspaceId, signatureId, payload);
            showToast('Signature updated!');
        }, 'Failed to update signature.');
    };

    const handleDeleteEmailSignature = async (signatureId) => {
        if (!selectedWorkspaceId || !signatureId) return;
        await run(async () => {
            await emailService.deleteEmailSignature(selectedWorkspaceId, signatureId);
            showToast('Signature deleted.');
        }, 'Failed to delete signature.');
    };

    const handleSetDefaultEmailSignature = async (signatureId) => {
        if (!user) {
            showToast('Sign in to update default signature.', 'error');
            return;
        }
        await run(async () => {
            await emailService.setDefaultEmailSignature(user.uid, signatureId);
            showToast(signatureId ? 'Default signature updated.' : 'Default signature cleared.');
        }, 'Failed to update default signature.');
    };

    const handleCreateEmailAttachment = async (attachmentData) => {
        if (!selectedWorkspaceId) return;
        const timestamp = Date.now();
        const extension = extractExtension(attachmentData.name);
        const payload = {
            name: (attachmentData.name || '').trim(),
            downloadURL: (attachmentData.downloadURL || attachmentData.url || '').trim(),
            storagePath: typeof attachmentData.storagePath === 'string' ? attachmentData.storagePath : '',
            contentType: attachmentData.contentType || 'application/octet-stream',
            size: attachmentData.size ? Number(attachmentData.size) : null,
            extension: typeof attachmentData.extension === 'string' ? attachmentData.extension : extension,
            createdAt: timestamp,
            updatedAt: timestamp
        };
        if (!payload.name) {
            showToast('Attachment name is required.', 'error');
            return;
        }
        if (!payload.downloadURL) {
            showToast('Attachment upload failed. Please try again.', 'error');
            return;
        }
        await run(async () => {
            await emailService.createEmailAttachment(selectedWorkspaceId, payload);
            showToast('Attachment saved!');
        }, 'Failed to save attachment.');
    };

    const handleUploadEmailAttachmentFile = async (file) => {
        if (!selectedWorkspaceId || !file) {
            showToast('Select a workspace before uploading files.', 'error');
            return { success: false, error: 'missing-workspace' };
        }
        const validation = validateEmailAttachmentFile(file);
        if (!validation.ok) {
            showToast(validation.message, 'error');
            return { success: false, error: validation.reason };
        }
        const workspaceId = selectedWorkspaceId;
        const storagePath = buildWorkspaceAssetPath(workspaceId, file.name);
        const extension = extractExtension(file.name);
        const displayName = (() => {
            const base = file.name?.replace(/\.[^/.]+$/, '') || 'Uploaded File';
            return base.trim() || 'Uploaded File';
        })();
        try {
            const downloadURL = await emailService.uploadEmailAttachmentFile(storagePath, file);
            await handleCreateEmailAttachment({
                name: displayName,
                downloadURL,
                storagePath,
                contentType: file.type || 'application/octet-stream',
                size: file.size || null,
                extension
            });
            return { success: true, downloadURL };
        } catch (error) {
            console.error('Error uploading attachment file:', error);
            showToast(writeErrorMessage(error, 'Failed to upload file. Check your connection and try again.'), 'error');
            return { success: false, error };
        }
    };

    const handleDeleteEmailAttachment = async (attachment) => {
        if (!selectedWorkspaceId || !attachment?.id) return;
        await run(async () => {
            const templateIdsUsingAttachment = emailTemplates
                .filter(template => Array.isArray(template.attachmentIds) && template.attachmentIds.includes(attachment.id))
                .map(template => template.id);
            await emailService.deleteEmailAttachment(selectedWorkspaceId, attachment, templateIdsUsingAttachment);
            showToast('Attachment deleted.');
        }, 'Failed to delete attachment.');
    };

    const handleRenameEmailAttachment = async (attachment, newName) => {
        if (!selectedWorkspaceId || !attachment?.id) return;
        const trimmed = (newName || '').trim();
        if (!trimmed || trimmed === attachment.name) return;
        try {
            await emailService.renameEmailAttachment(selectedWorkspaceId, attachment.id, trimmed);
            showToast('Attachment renamed.');
        } catch (error) {
            console.error('Error renaming attachment:', error);
            showToast(writeErrorMessage(error, 'Failed to rename attachment.'), 'error');
            throw error;
        }
    };

    return {
        handleCreateEmailTemplate,
        handleUpdateEmailTemplate,
        handleDeleteEmailTemplate,
        handleImportEmailTemplates,
        handleCreateChecklistTemplate,
        handleUpdateChecklistTemplate,
        handleDeleteChecklistTemplate,
        handleCreateEmailTemplateFolder,
        handleUpdateEmailTemplateFolder,
        handleDeleteEmailTemplateFolder,
        handleMoveEmailTemplates,
        handleCreateEmailSignature,
        handleUpdateEmailSignature,
        handleDeleteEmailSignature,
        handleSetDefaultEmailSignature,
        handleUploadEmailAttachmentFile,
        handleCreateEmailAttachment,
        handleDeleteEmailAttachment,
        handleRenameEmailAttachment,
    };
}
